import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { CostTrackingService } from '../../services/cost-tracking.service';
import { FilterService } from '../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { CostCategoryItem } from '../../models/cost-tracking-response.model';
import { BUDGET_CATEGORY_LABELS } from '../../models/cost-tracking-request.model';
import { DateIntervalFilterData, WidgetFilterData } from '../../../../_shared/models/filters/widget-filter-data.model';
import { SummaryRequest } from '../../../../_shared/models';
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphTrendIconComponent } from '../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { DateFilterComponent } from '../../../../_shared/components/filters/date-filter/date-filter.component';
import { DateInterval } from '../../../../_shared/enums/date-interval.enum';
import { formatCost } from '../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { hasCompleteDateRange } from '../../../../_shared/helpers/widget-filter.helper';
import { AuthService } from '../../../../_shared/services/auth.service';
import { formatCurrencyAmount } from '../../../../_shared/helpers/currency/currency-format.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../_shared/pipes/lookup-label.pipe';

@Component({
  selector: 'app-cost-distribution-category',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, EmptyStateComponent, GraphTrendIconComponent, DateFilterComponent, TranslocoPipe, LookupLabelPipe],
  templateUrl: './cost-distribution-category.component.html',
  styleUrl: './cost-distribution-category.component.scss'
})
export class CostDistributionCategoryComponent implements OnInit, OnDestroy {
  protected readonly budgetCategoryLabels = BUDGET_CATEGORY_LABELS;

  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private cancelSubject: Subject<void> = new Subject<void>();
  private authService = inject(AuthService);

  title: string = inject(TranslocoService).translate('costTracking.charts.costDistributionByCategory');
  isLoading: boolean = true;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  totalValue: number = 0;
  categories: CostCategoryItem[] = [];
  totalPercentageChange: number = 0;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  private filterSubscription: Subscription;

  constructor() {
    this.dateFilter = {} as DateIntervalFilterData;
    this.filterSubscription = this.filterService.filterUpdated$.subscribe(async (filter) => {
      this.applyPageFilter(filter);
      await this.loadData();
    });

    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  ngOnInit(): void {
    this.applyPageFilter(this.filterService.filter);
    void this.loadData();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const response = await this.costTrackingService.getCostCategoryDistribution(request, this.cancelSubject);

      this.categories = response.data?.categories ?? [];
      this.totalValue = response.data?.totalValue ?? 0;
      this.totalPercentageChange = response.data?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.categories = [];
        }
      });
    }
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.widgetFilter = { ...this.widgetFilter, ...filter };
    void this.loadData();
  }

  formatCost(value: number): string {
    return formatCost(value);
  }

  formatBudget(value: number): string {
    return formatCurrencyAmount(this.currencySymbol, value);
  }

  formatPercentChange(): string {
    return this.formatSignedPercentage(this.totalPercentageChange);
  }

  formatSignedPercentage(value: number): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false;
  }

  hasData(): boolean {
    return this.categories.length > 0;
  }

  private applyPageFilter(filter: WidgetFilterData): void {
    this.widgetFilter = filter;
    if (hasCompleteDateRange(filter)) this.dateFilter = this.toDateFilter(filter);
  }

  private toDateFilter(filter: DateIntervalFilterData): DateIntervalFilterData {
    return {
      startDate: filter.startDate,
      endDate: filter.endDate,
      granularity: filter.granularity,
      previousCycleStartDate: filter.previousCycleStartDate,
      previousCycleEndDate: filter.previousCycleEndDate,
      interval: filter.interval,
      isCustomDate: filter.isCustomDate,
    };
  }
}